import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { ErrorField } from '@/components/error-field'
import * as Input from '@/components/input'
import { usePaymentJustification } from '@/components/payment-justification/hooks/use-payment-justification'
import { PaymentJustificationForm } from '@/components/payment-justification/payment-justification'
import { Textarea } from '@/components/textarea'
import { PaymentJustification } from '@/models/payment-justification'
import { PaymentJustificationStatusFormValues } from '@/schemas/backoffice/payment-justification-modal-schema'
import api from '@/services/api'
import { generateUrl } from '@/utils/generate-url'

import { Skeleton } from '../skeleton'

interface PaymentJustificationContentProps {
  isShowRemoveButton?: boolean
  handleClosePaymentJustificationModal?: () => void
  handleUpdateInstallments?: (
    installmentId: string,
    justificationId: string,
  ) => void
  handleOpenDeletePaymentJustificationModal?: (
    value: boolean,
    id: string,
  ) => void
}
export function PaymentJustificationContent({
  isShowRemoveButton = false,
  handleUpdateInstallments,
  handleClosePaymentJustificationModal,
  handleOpenDeletePaymentJustificationModal,
}: PaymentJustificationContentProps) {
  const {
    reset,
    register,
    watch,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<PaymentJustificationStatusFormValues>()

  const isDisabled = !isShowRemoveButton
  const justificationFile = watch('justification_file')

  const [loadingIndex, setLoadingIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { fileSizeMB, handleFileValidation } = usePaymentJustification({
    justificationFileLength: justificationFile?.length ?? 0,
    testId: 'payment-justification',
  })

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const validatedFiles = handleFileValidation(event)

    if (!validatedFiles) return

    if (validatedFiles.length > 0) {
      const currentFiles = getValues('justification_file') || []
      const newFiles = Array.from(validatedFiles).map((file) => ({
        id: '', // ID vai ser retornado do back na requisição do get justification by id
        url: URL.createObjectURL(file),
        name: '',
        file,
      }))

      const updatedFiles = [...currentFiles, ...newFiles]

      setValue('justification_file', updatedFiles, {
        shouldValidate: true,
      })

      event.target.value = ''
    }
  }

  async function handleSaveJustificationLocal(
    data: PaymentJustificationStatusFormValues,
  ) {
    const formData = new FormData()

    data.justification_file.forEach((justification) => {
      if (justification.file) {
        formData.append('file', justification.file)
      }
    })
    formData.append('comment', data.comment)
    formData.append('send_email', String(data.send_email))

    const justificationId = data.payment_justification_id

    if (justificationId) {
      return api
        .put(`/justifications/${justificationId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(() => {
          toast.success('Justificativa atualizada com sucesso.')
          if (handleClosePaymentJustificationModal) {
            handleClosePaymentJustificationModal()
          }
        })
        .catch((error) => toast.error(error.response.data.message))
    }

    const installmentId = data.installment_id

    if (!installmentId) return toast.warning('ID da parcela não encontrado.')

    await api
      .post(`/installments/${installmentId}/justifications`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        toast.success('Justificativa criada com sucesso.')
        if (handleClosePaymentJustificationModal && handleUpdateInstallments) {
          handleClosePaymentJustificationModal()
          handleUpdateInstallments(installmentId, response.data)
        }
      })
      .catch((error) => toast.error(error.response.data.message))
  }

  useEffect(() => {
    const justificationId = getValues('payment_justification_id')
    if (!justificationId) return

    const abortController = new AbortController()
    const signal = abortController.signal

    async function getJustificationById() {
      setIsLoading(true)
      await api
        .get<PaymentJustification>(`/justifications/${justificationId}`, {
          signal,
        })
        .then(({ data }) => {
          reset({
            ...getValues(),
            comment: data.comentario,
            send_email: data.send_email,
            justification_file: data.arquivos.map((file) => ({
              id: file.id,
              url: file.url,
              name: file.nome,
              file: undefined,
            })),
          })
        })
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setIsLoading(false))
    }

    getJustificationById()

    return () => {
      abortController.abort()
    }
  }, [getValues, reset])

  const handleOpePaymentJustification = useCallback(
    async (url: string, index: number) => {
      setLoadingIndex(index)
      await generateUrl({ value: url }).finally(() => {
        setLoadingIndex(null)
      })
    },
    [],
  )

  function handleRemoveJustificationFile(index: number) {
    const currentFiles = getValues('justification_file')
    const updatedFiles = currentFiles.filter((_, i) => i !== index)

    setValue('justification_file', updatedFiles, {
      shouldValidate: true,
    })
  }

  const handleDeletePaymentJustification = useCallback(
    async (id: string) => {
      const justificationId = getValues('payment_justification_id')

      if (!justificationId || !handleOpenDeletePaymentJustificationModal) return

      handleOpenDeletePaymentJustificationModal(true, id)
    },
    [getValues, handleOpenDeletePaymentJustificationModal],
  )

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PaymentJustificationForm.Header>
          <h3 className="font-bold">
            Documentos anexados {justificationFile?.length ?? 0}/3
          </h3>
          <p>
            São permitidos documentos com tamanho máximo de {fileSizeMB}MB cada.
          </p>
        </PaymentJustificationForm.Header>

        <div>
          <Skeleton className="h-14 rounded-lg border border-transparent" />
          <PaymentJustificationForm.ListInputRoot
            disabled={justificationFile?.length >= 3 || isDisabled}
            onChange={handleFileChange}
            id="payment-justification-file"
          />
        </div>

        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-14 rounded-lg border border-transparent"
          />
        ))}

        <Skeleton className="min-h-32 rounded-lg" />

        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-5 w-full rounded-lg md:w-72" />
        </div>
      </div>
    )
  }

  return (
    <PaymentJustificationForm.Root
      id="payment-justification-form"
      onSubmit={handleSubmit(handleSaveJustificationLocal)}
    >
      <PaymentJustificationForm.Header>
        <h3 className="font-bold">
          Documentos anexados {justificationFile?.length ?? 0}/3
        </h3>
        <p>
          São permitidos documentos com tamanho máximo de {fileSizeMB}MB cada.
        </p>
      </PaymentJustificationForm.Header>
      <div>
        <PaymentJustificationForm.UploadFile
          aria-disabled={justificationFile?.length >= 3 || isDisabled}
          htmlFor="payment-justification-file"
          hasError={!!errors.justification_file}
        />
        {errors.justification_file?.message && (
          <ErrorField>{errors.justification_file.message}</ErrorField>
        )}
        <PaymentJustificationForm.ListInputRoot
          disabled={justificationFile?.length >= 3 || isDisabled}
          onChange={handleFileChange}
          id="payment-justification-file"
        />
      </div>
      <PaymentJustificationForm.ListFileRoot>
        {justificationFile?.map((fileObj, index) => {
          return (
            <PaymentJustificationForm.ListFileContent
              key={`${index}-${fileObj.url}-${fileObj.id}`}
              isEditing={fileObj.id !== ''}
              isLoadingJustificationUrl={loadingIndex === index}
              handleOpePaymentJustification={() => {
                return handleOpePaymentJustification(fileObj.url, index)
              }}
              isShowRemoveButton={isShowRemoveButton}
              url={fileObj.url}
              fileName={fileObj.file?.name ?? fileObj.name}
              handleRemoveJustificationFile={() => {
                if (fileObj.id) {
                  return handleDeletePaymentJustification(fileObj.id)
                }
                return handleRemoveJustificationFile(index)
              }}
            />
          )
        })}
      </PaymentJustificationForm.ListFileRoot>
      <div>
        <Input.Label htmlFor="comment" mandatory>
          Comentário
        </Input.Label>
        <Textarea
          isResize
          id="comment"
          disabled={isDisabled}
          placeholder="Insira um comentário para a justificativa"
          maxLength={5000}
          withError={!!errors.comment?.message}
          count={watch('comment')?.length ?? 0}
          limit={5000}
          {...register('comment')}
        />
        {errors.comment?.message && (
          <ErrorField>{errors.comment.message}</ErrorField>
        )}
      </div>
      {/* TODO: descomentar quando o cliente decidir se quer ou não enviar a
      justificativa por e-mail para o usuário */}
      {/* <div
        aria-disabled={isDisabled}
        className="flex items-center gap-2 aria-disabled:cursor-not-allowed aria-disabled:*:cursor-not-allowed"
      >
        <input
          type="checkbox"
          id="send-to-mail"
          disabled={isDisabled}
          {...register('send_email')}
        />
        <label htmlFor="send-to-mail" className="text-monochromatic-500">
          Deseja enviar a justificativa por e-mail para o usuário?
        </label>
      </div> */}
    </PaymentJustificationForm.Root>
  )
}
